import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErrorMessage } from "@/api/client";
import { useParticipantAuth } from "@/context/ParticipantAuthContext";
import { ProgressRing } from "@/components/ProgressRing";
import { DeadlinePill } from "@/components/DeadlinePill";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  LogOut, ClipboardCheck, Lock, CheckCircle2, Clock, ArrowRight, Loader2, ChevronDown,
  BarChart3, Users, Lightbulb, Sparkles,
} from "lucide-react";

const HERO_IMAGE = "https://images.pexels.com/photos/18591087/pexels-photo-18591087.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=1600";

const WEEK_IMAGES = {
  1: "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=80&w=640",
  2: "https://images.pexels.com/photos/7640470/pexels-photo-7640470.jpeg?auto=compress&cs=tinysrgb&h=420&w=640",
  3: "https://images.unsplash.com/photo-1588196749597-9ff075ee6b5b?crop=entropy&cs=srgb&fm=jpg&q=80&w=640",
  4: "https://images.unsplash.com/photo-1532622785990-d2c36a76f5a6?crop=entropy&cs=srgb&fm=jpg&q=80&w=640",
  5: "https://images.unsplash.com/photo-1672380135241-c024f7fbfa13?crop=entropy&cs=srgb&fm=jpg&q=80&w=640",
};

const SUB_STATUS_LABEL = {
  sin_registro: { label: "Sin registro", cls: "bg-slate-100 text-slate-500 border-slate-200" },
  draft: { label: "Borrador guardado", cls: "bg-amber-50 text-amber-800 border-amber-200" },
  realizado: { label: "Actividad registrada", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  no_realizado: { label: "Dificultad informada", cls: "bg-rose-50 text-rose-700 border-rose-200" },
};

const HIGHLIGHTS = [
  { icon: BarChart3, text: "Nuevas herramientas para tu gestión" },
  { icon: Users, text: "Aprendizaje aplicado a tu realidad" },
  { icon: Lightbulb, text: "Una comunidad que te impulsa" },
];

export default function ParticipantPortal() {
  const { logout } = useParticipantAuth();
  const [portal, setPortal] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const load = async () => {
    try {
      const { data } = await api.get("/participant/portal");
      setPortal(data);
    } catch (err) {
      setError(apiErrorMessage(err));
    }
  };

  useEffect(() => { load(); }, []);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  if (error) {
    return <div className="min-h-screen flex items-center justify-center text-rose-600 text-sm font-semibold">{error}</div>;
  }
  if (!portal) {
    return <div className="min-h-screen flex items-center justify-center bg-slate-50"><Loader2 className="w-8 h-8 text-brand-accent animate-spin" /></div>;
  }

  const firstName = portal.participant.full_name.split(" ")[0];
  const overallProgress = Math.round(
    ((portal.diagnostic_status === "completado" ? 1 : 0) + portal.delivered_count) / (1 + portal.total_weeks) * 100
  );

  return (
    <div className="min-h-screen bg-[color:var(--brand-bg)]">
      {/* Barra de navegación superior */}
      <nav className="sticky top-0 z-40 bg-white border-b border-slate-200/80" data-testid="portal-navbar">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <img src="/brand/inside-logo.png" alt="INSIDE" className="h-7 w-auto" />
            <div className="hidden sm:block w-px h-6 bg-slate-200" />
            <span className="hidden sm:inline text-[11px] font-extrabold text-slate-700 tracking-wide">ANID</span>
          </div>
          <div className="hidden md:flex items-center gap-1">
            <span className="px-3.5 py-2 rounded-lg text-xs font-bold text-brand-accent bg-[color:var(--brand-sky-tint)]">Inicio</span>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="portal-user-menu-trigger" className="flex items-center gap-2 pl-1 pr-2.5 py-1.5 rounded-full hover:bg-slate-100 transition-colors">
                <span className="w-7 h-7 rounded-full bg-brand-slate text-white text-[11px] font-bold flex items-center justify-center">{firstName[0]}</span>
                <span className="hidden sm:block text-left leading-tight">
                  <span className="block text-xs font-bold text-slate-700">{firstName}</span>
                  <span className="block text-[10px] text-slate-400">Participante</span>
                </span>
                <ChevronDown className="w-3.5 h-3.5 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <DropdownMenuItem disabled className="text-xs text-slate-400">{portal.participant.unit}</DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="portal-logout-btn" onClick={handleLogout} className="text-xs font-bold text-rose-600 cursor-pointer gap-2">
                <LogOut className="w-3.5 h-3.5" /> Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      {/* Hero institucional — franja compacta con foto muy sutil (según lineamientos) */}
      <section className="relative overflow-hidden bg-[color:var(--brand-navy)]" data-testid="portal-hero">
        <div className="absolute inset-0 bg-cover bg-center opacity-[0.14] mix-blend-luminosity" style={{ backgroundImage: `url(${HERO_IMAGE})` }} />
        <div className="absolute inset-0 bg-gradient-to-r from-[color:var(--brand-navy)] via-[color:var(--brand-navy)]/95 to-[color:var(--brand-navy)]/75" />
        <div className="relative max-w-6xl mx-auto px-5 pt-9 pb-16 sm:pt-11 sm:pb-20">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div className="max-w-lg">
              <p className="text-[10px] sm:text-xs font-bold uppercase tracking-[0.22em] text-white/60 mb-2">Curso de Habilidades para el Liderazgo</p>
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-testid="portal-welcome-title">
                Hola, {firstName} <span aria-hidden>👋</span>
              </h1>
              <p className="text-sm text-white/70 mt-2 leading-relaxed">{portal.participant.unit}. Completa tu diagnóstico inicial y registra tus actividades prácticas de cada taller.</p>
            </div>
            <p className="hidden lg:block max-w-[220px] text-right italic text-xs text-white/45">“Personas que impulsan un mejor futuro”</p>
          </div>
        </div>

        {/* Card flotante de progreso */}
        <div className="relative max-w-6xl mx-auto px-5 -mt-9 sm:-mt-10 pb-8">
          <div className="bg-white rounded-2xl border border-[color:var(--brand-border)] shadow-[var(--shadow-md)] p-6 flex flex-col sm:flex-row items-center gap-6 sm:gap-8">
            <div className="flex items-center gap-4 shrink-0">
              <ProgressRing value={overallProgress} size={64} />
              <div>
                <p className="text-[10px] font-bold uppercase text-[color:var(--text-secondary)]">Tu progreso</p>
                <p className="text-sm font-extrabold text-[color:var(--text-primary)]" data-testid="portal-overall-progress">{portal.delivered_count + (portal.diagnostic_status === "completado" ? 1 : 0)} de {portal.total_weeks + 1} etapas</p>
              </div>
            </div>
            <div className="hidden sm:block w-px h-14 bg-[color:var(--brand-border)] shrink-0" />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 flex-1">
              {HIGHLIGHTS.map((h) => (
                <div key={h.text} className="flex items-center gap-2.5">
                  <div className="w-8 h-8 rounded-full bg-[color:var(--brand-sky-tint)] flex items-center justify-center shrink-0"><h.icon className="w-4 h-4 text-brand-accent" /></div>
                  <span className="text-[11px] font-semibold text-[color:var(--text-secondary)] leading-tight">{h.text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-6xl mx-auto px-5 pb-14 space-y-6">
        <div className="flex items-center justify-between gap-3 pt-2">
          <div>
            <h2 className="font-display text-lg font-extrabold text-slate-900">Tu ruta de aprendizaje</h2>
            <p className="text-xs text-slate-500 mt-0.5">Diagnóstico Inicial y Actividades Prácticas del curso</p>
          </div>
        </div>

        {!portal.activities_unlocked && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 flex items-center gap-3 text-orange-800 text-xs font-semibold" data-testid="activities-locked-notice">
            <Lock className="w-4 h-4 shrink-0" /> Completa primero el diagnóstico inicial para desbloquear las actividades prácticas.
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="portal-activity-grid">
          {/* Tarjeta diagnóstico */}
          <button
            data-testid="diagnostic-action-btn"
            onClick={() => navigate("/diagnostico")}
            className="text-left bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04),0_12px_24px_rgba(15,23,42,0.06)] p-5 flex flex-col hover:-translate-y-0.5 hover:shadow-[0_1px_0_rgba(15,23,42,0.06),0_18px_36px_rgba(15,23,42,0.10)] transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-inside-blue)]"
          >
            <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase px-2.5 py-1 rounded-full bg-[color:var(--brand-inside-blue)] text-white w-fit mb-3"><Sparkles className="w-3 h-3" /> Paso inicial</span>
            <div className="w-11 h-11 rounded-xl bg-[color:var(--brand-sky-tint)] flex items-center justify-center mb-3"><ClipboardCheck className="w-5 h-5 text-brand-accent" /></div>
            <h3 className="font-display font-bold text-slate-900 text-sm mb-1.5">Diagnóstico Inicial</h3>
            <p className="text-xs text-slate-500 flex-1">Evalúa tus hábitos directivos y resuelve 8 casos situacionales.</p>
            <div className="flex items-center gap-3 text-[10px] font-semibold text-slate-400 mt-3">
              <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> 15 minutos</span>
              <span className="flex items-center gap-1"><BarChart3 className="w-3 h-3" /> Autodiagnóstico</span>
            </div>
            <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-bold text-white bg-[color:var(--brand-inside-blue)] hover:bg-[color:var(--brand-inside-blue-2)] rounded-lg px-4 py-2.5 justify-center transition-colors">
              {portal.diagnostic_status === "completado" ? "Ver informe de resultados" : portal.diagnostic_status === "en_progreso" ? "Continuar diagnóstico" : "Comenzar diagnóstico"} <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </button>

          {portal.weeks.map((w) => {
            const subBadge = SUB_STATUS_LABEL[w.submission_status] || SUB_STATUS_LABEL.sin_registro;
            const locked = !portal.activities_unlocked || !w.published || !w.is_open;
            return (
              <button
                key={w.week}
                data-testid={`activity-card-week-${w.week}`}
                onClick={() => !locked && navigate(`/actividades/${w.week}`)}
                disabled={locked}
                className={`text-left bg-white rounded-2xl border border-slate-200/70 shadow-[0_1px_0_rgba(15,23,42,0.04),0_12px_24px_rgba(15,23,42,0.06)] overflow-hidden flex flex-col transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--brand-inside-blue)] ${locked ? "opacity-70 cursor-not-allowed" : "hover:-translate-y-0.5 hover:shadow-[0_1px_0_rgba(15,23,42,0.06),0_18px_36px_rgba(15,23,42,0.10)]"}`}
              >
                <div className="relative h-28 shrink-0">
                  <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: `url(${WEEK_IMAGES[w.week]})` }} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-black/5 to-transparent" />
                  <span className="absolute bottom-2 left-3 text-[10px] font-black uppercase text-white tracking-wide">Semana {w.week}</span>
                  {locked ? <Lock className="absolute top-2.5 right-2.5 w-4 h-4 text-white/90" /> : w.submission_status === "realizado" && <CheckCircle2 className="absolute top-2.5 right-2.5 w-4 h-4 text-emerald-400" />}
                </div>
                <div className="p-4 flex flex-col flex-1">
                  <h4 className="font-display text-sm font-bold text-slate-900 mb-1.5 leading-snug">{w.topic}</h4>
                  <p className="text-[11px] text-slate-400 flex-1">{w.published ? "Taller práctico del programa." : "Se publicará próximamente."}</p>
                  <div className="flex items-center justify-between gap-2 mt-3 flex-wrap">
                    <span className={`inline-block text-[10px] font-bold uppercase px-2.5 py-1 rounded-full border ${subBadge.cls}`}>
                      {!w.published ? "Bloqueada" : subBadge.label}
                    </span>
                    <DeadlinePill dueDate={w.due_date} published={w.published} />
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </main>

      <footer className="bg-[color:var(--brand-navy)] text-white px-5 sm:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-2 mt-8" data-testid="portal-footer">
        <p className="text-[10px] font-semibold text-slate-300">Herramienta de apoyo al programa desarrollada por INSIDE® Training &amp; Learning</p>
        <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Liderazgo hoy para grandes mañanas</p>
      </footer>
    </div>
  );
}
